import React, { useState, useCallback, useEffect } from "react";
import { RightPanelService } from "../../services/rightPanelService";
import { useTranslation } from "../../hooks/useTranslation";
import AIExpansionModal from "../ai/AIExpansionModal";
import Modal from "../common/Modal";
import { expandStep, needsExpansion, getLoadingMessage } from "../../services/stepExpansionService";

const RightPanelRenderer = ({ 
  node, 
  planId, 
  onClose, 
  onFullscreenChange, 
  isDemo = false,
  // NEW: Props for lazy loading context
  onStepExpanded,
  expansionContext = null, // { goal, formData, subcategory, planStructure }
}) => {
  const t = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDeadlineInput, setShowDeadlineInput] = useState(false);
  const [deadlineDate, setDeadlineDate] = useState(node?.deadline || '');
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState(node?.notes || '');
  const [nodeStatus, setNodeStatus] = useState(node?.status || 'standby');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [newTodoText, setNewTodoText] = useState('');
  const [isAddingTodo, setIsAddingTodo] = useState(false);
  const [localTodos, setLocalTodos] = useState(node?.todos || []);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [deleteTodoModal, setDeleteTodoModal] = useState({ show: false, todoId: null, todoText: '' });
  
  // NEW: Lazy loading states
  const [isExpanding, setIsExpanding] = useState(false);
  const [expansionError, setExpansionError] = useState(null);
  const [expansionPhase, setExpansionPhase] = useState('starting');
  
  // Ref for the scrollable panel container
  const panelRef = React.useRef(null);

  // Initialize the service
  const rightPanelService = useCallback(() => {
    if (!planId) {
      return null;
    }
    return new RightPanelService(planId);
  }, [planId]);

  // Clear error after 5 seconds
  React.useEffect(() => {
    if (saveError) {
      const timer = setTimeout(() => setSaveError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveError]);

  // Scroll to top of the panel when node changes
  React.useEffect(() => {
    if (panelRef.current && node?.id) {
      panelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [node?.id]); // Trigger when node ID changes

  // Update deadline date when node changes
  React.useEffect(() => {
    setDeadlineDate(node?.deadline || '');
    setShowDeadlineInput(false); // Reset deadline input when switching nodes
    setIsEditingDeadline(false); // Reset edit mode when switching nodes
  }, [node?.id]); // Watch for node ID changes
  
  // Update deadline date when node.deadline changes (but only if not editing)
  React.useEffect(() => {
    if (!isEditingDeadline) {
      setDeadlineDate(node?.deadline || '');
    }
  }, [node?.deadline, isEditingDeadline]);

  // Update notes text when node changes
  React.useEffect(() => {
    setNotesText(node?.notes || '');
    setIsEditingNotes(false); // Reset edit mode when switching nodes
  }, [node?.id, node?.notes]); // Watch for node ID changes too

  // Update node status when node changes
  React.useEffect(() => {
    setNodeStatus(node?.status || 'standby');
  }, [node?.id, node?.status]);

  // Update local todos when node changes
  React.useEffect(() => {
    setLocalTodos(node?.todos || []);
  }, [node?.id, node?.todos]); // Sync when node ID or todos change

  // Reset expansion states when node changes
  useEffect(() => {
    setIsExpanding(false);
    setExpansionError(null);
    setExpansionPhase('starting');
  }, [node?.id]);

  // Auto-expand skeleton steps when viewed (if context is available)
  useEffect(() => {
    const expandSkeletonStep = async () => {
      // Skip if demo mode, no context, or step doesn't need expansion
      if (isDemo || !expansionContext || !node || !planId || node.id === 'start') {
        return;
      }
      
      // Check if step needs expansion
      if (!needsExpansion(node)) {
        return;
      }
      
      console.log(`Step ${node.id} needs expansion, starting auto-expand...`);
      setIsExpanding(true);
      setExpansionError(null);
      setExpansionPhase('loading');
      
      try {
        setExpansionPhase('processing');
        const result = await expandStep(planId, node.id, node, expansionContext);
        
        if (result.success) {
          setExpansionPhase('complete');
          // Notify parent component of successful expansion
          if (onStepExpanded && result.data?.comprehensiveStepData) {
            onStepExpanded(node.id, {
              ...node,
              ...result.data.comprehensiveStepData,
              comprehensiveLoaded: true,
            });
          }
        } else {
          setExpansionPhase('error');
          setExpansionError(result.error?.message || 'Failed to load step details');
        }
      } catch (error) {
        console.error('Error expanding step:', error);
        setExpansionPhase('error');
        setExpansionError(error.message || 'Failed to load step details');
      } finally {
        setIsExpanding(false);
      }
    };
    
    expandSkeletonStep();
  }, [node?.id, node?.comprehensiveLoaded, planId, isDemo, expansionContext, onStepExpanded]);

  // Manual retry handler
  const handleRetryExpansion = async () => {
    if (!node || !planId || !expansionContext) return;
    
    setIsExpanding(true);
    setExpansionError(null);
    setExpansionPhase('retrying');
    
    try {
      const result = await expandStep(planId, node.id, node, expansionContext);
      
      if (result.success) {
        setExpansionPhase('complete');
        if (onStepExpanded && result.data?.comprehensiveStepData) {
          onStepExpanded(node.id, {
            ...node,
            ...result.data.comprehensiveStepData,
            comprehensiveLoaded: true,
          });
        }
      } else {
        setExpansionPhase('error');
        setExpansionError(result.error?.message || 'Failed to load step details');
      }
    } catch (error) {
      setExpansionPhase('error');
      setExpansionError(error.message || 'Failed to load step details');
    } finally {
      setIsExpanding(false);
    }
  };

  const handleFullscreenToggle = () => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    if (onFullscreenChange) {
      onFullscreenChange(newFullscreenState);
    }
  };

  const handleEditDeadline = () => {
    setIsEditingDeadline(true);
    setShowDeadlineInput(true);
    setDeadlineDate(node?.deadline || '');
  };

  const handleSaveDeadline = async () => {
    if (deadlineDate && node?.id) {
      if (isDemo) {
        // In demo mode, just update local state via callback
        if (node.onDeadlineChange) {
          node.onDeadlineChange(deadlineDate);
        }
        setShowDeadlineInput(false);
        setIsEditingDeadline(false);
        return;
      }

      if (!planId) {
        setSaveError('Service not available - planId missing');
        return;
      }

      setIsSaving(true);
      try {
        const service = rightPanelService();
        if (service) {
          const result = await service.updateDeadline(node.id, deadlineDate);
          if (result.success) {
            // Update the node with the new deadline
            if (node.onDeadlineChange) {
              node.onDeadlineChange(deadlineDate);
            }
            setShowDeadlineInput(false);
            setIsEditingDeadline(false);
          } else {
            console.error('Failed to save deadline:', result.error);
            setSaveError(t('rightPanel.failedToSaveDeadline').replace('{error}', result.error));
          }
        } else {
          console.error('Service is null - planId might be missing');
          setSaveError(t('rightPanel.serviceNotAvailable'));
        }
      } catch (error) {
        console.error('Error saving deadline:', error);
        setSaveError(t('rightPanel.errorSavingDeadline').replace('{error}', error.message));
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error('Missing required data:', { deadlineDate, planId, nodeId: node?.id });
      setSaveError(t('rightPanel.missingDataDeadline'));
    }
  };

  const handleCancelDeadline = () => {
    setDeadlineDate(node?.deadline || '');
    setShowDeadlineInput(false);
    setIsEditingDeadline(false);
  };

  const handleRemoveDeadline = async () => {
    if (node?.id) {
      if (isDemo) {
        // In demo mode, just update local state via callback
        if (node.onDeadlineChange) {
          node.onDeadlineChange(''); // Pass empty string to remove deadline
        }
        setDeadlineDate('');
        return;
      }

      if (!planId) {
        return;
      }

      setIsSaving(true);
      try {
        const service = rightPanelService();
        if (service) {
          const result = await service.updateDeadline(node.id, '');
          if (result.success) {
            if (node.onDeadlineChange) {
              node.onDeadlineChange(''); // Pass empty string to remove deadline
            }
            setDeadlineDate('');
          } else {
            console.error('Failed to remove deadline:', result.error);
            setSaveError(t('rightPanel.failedToRemoveDeadline').replace('{error}', result.error));
          }
        }
      } catch (error) {
        console.error('Error removing deadline:', error);
        setSaveError(t('rightPanel.errorRemovingDeadline').replace('{error}', error.message));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleTodoToggle = async (todoId) => {

    if (node?.id && localTodos) {
      // Update local todos state
      const updatedTodos = localTodos.map(todo =>
        todo.id === todoId ? { ...todo, done: !todo.done } : todo
      );
      setLocalTodos(updatedTodos);

      // Also update the node's todos to keep them in sync
      if (node.todos) {
        node.todos = updatedTodos;
      }

      if (node.onTodoToggle) {
        node.onTodoToggle(todoId, updatedTodos);
      }

      if (isDemo) {
        // In demo mode, skip Firestore call
        return;
      }

      if (!planId) {
        return;
      }

      setIsSaving(true);
      try {
        const service = rightPanelService();
        if (service) {
          const result = await service.toggleTodo(node.id, todoId, localTodos);
          if (!result.success) {
            console.error('Failed to toggle todo:', result.error);
            setSaveError(t('rightPanel.failedToToggleTodo').replace('{error}', result.error));
            // Revert local state on error
            setLocalTodos(localTodos);
            if (node.todos) {
              node.todos = localTodos;
            }
          }
        }
      } catch (error) {
        console.error('Error toggling todo:', error);
        setSaveError(t('rightPanel.errorTogglingTodo').replace('{error}', error.message));
        // Revert local state on error
        setLocalTodos(localTodos);
        if (node.todos) {
          node.todos = localTodos;
        }
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error('Missing required data for todo toggle:', { planId, nodeId: node?.id, localTodos });
    }
  };

  const handleRemoveTodo = async (todoId) => {
    if (node?.id && localTodos) {
      // Store original todos for potential rollback
      const originalTodos = localTodos;
      
      // Update local todos state
      const updatedTodos = localTodos.filter(todo => todo.id !== todoId);
      setLocalTodos(updatedTodos);

      // Also update the node's todos to keep them in sync
      if (node.todos) {
        node.todos = updatedTodos;
      }

      if (node.onTodoRemove) {
        node.onTodoRemove(todoId, updatedTodos);
      }

      if (isDemo) {
        // In demo mode, skip Firestore call
        return;
      }

      if (!planId) {
        return;
      }

      setIsSaving(true);
      try {
        const service = rightPanelService();
        if (service) {
          // Pass updatedTodos (the filtered array) instead of localTodos
          const result = await service.removeTodo(node.id, todoId, updatedTodos);
          if (!result.success) {
            console.error('Failed to remove todo:', result.error);
            setSaveError(t('rightPanel.failedToRemoveTodo').replace('{error}', result.error));
            // Revert local state on error
            setLocalTodos(originalTodos);
            if (node.todos) {
              node.todos = originalTodos;
            }
            // Also notify parent to revert
            if (node.onTodoRemove) {
              node.onTodoRemove(todoId, originalTodos);
            }
          }
        }
      } catch (error) {
        console.error('Error removing todo:', error);
        setSaveError(t('rightPanel.errorRemovingTodo').replace('{error}', error.message));
        // Revert local state on error
        setLocalTodos(originalTodos);
        if (node.todos) {
          node.todos = originalTodos;
        }
        // Also notify parent to revert
        if (node.onTodoRemove) {
          node.onTodoRemove(todoId, originalTodos);
        }
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error('Missing required data for todo removal:', { planId, nodeId: node?.id, localTodos });
    }
  };

  const handleSaveNotes = async () => {
    if (node?.id) {
      if (isDemo) {
        // In demo mode, just update local state via callback
        if (node.onNotesChange) {
          node.onNotesChange(notesText);
        }
        setIsEditingNotes(false);
        return;
      }

      if (!planId) {
        setSaveError(t('rightPanel.serviceNotAvailable'));
        return;
      }

      setIsSaving(true);
      try {
        const service = rightPanelService();
        if (service) {
          const result = await service.updateNotes(node.id, notesText);
          if (result.success) {
            if (node.onNotesChange) {
              node.onNotesChange(notesText);
            }
            setIsEditingNotes(false);
          } else {
            console.error('Failed to save notes:', result.error);
            setSaveError(t('rightPanel.failedToSaveNotes').replace('{error}', result.error));
          }
        }
      } catch (error) {
        console.error('Error saving notes:', error);
        setSaveError(t('rightPanel.errorSavingNotes').replace('{error}', error.message));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancelNotes = () => {
    setNotesText(node?.notes || '');
    setIsEditingNotes(false);
  };

  const handleAddNewTodo = async () => {

    if (newTodoText.trim() && node?.id) {
      // Create new todo with unique ID
      const newTodo = {
        id: `todo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: newTodoText.trim(),
        done: false,
        createdAt: new Date().toISOString()
      };

      // Update local todos state
      const updatedTodos = [...(localTodos || []), newTodo];
      setLocalTodos(updatedTodos);

      // Also update the node's todos to keep them in sync
      if (node.todos) {
        node.todos = updatedTodos;
      }

      // Add the new todo to the node's todos array via callback
      if (node.onTodoAdd) {
        node.onTodoAdd(newTodo);
      }

      setNewTodoText('');

      if (isDemo) {
        // In demo mode, skip Firestore call
        return;
      }

      if (!planId) {
        return;
      }

      setIsAddingTodo(true);
      try {
        const service = rightPanelService();
        if (service) {
          const currentTodos = localTodos || [];
          const todoTextToAdd = newTodoText.trim();
          const result = await service.addTodo(node.id, todoTextToAdd, currentTodos);
          if (!result.success) {
            console.error('Failed to add todo:', result.error);
            setSaveError(t('rightPanel.failedToAddTodo').replace('{error}', result.error));
            // Revert local state on error
            setLocalTodos(localTodos || []);
            if (node.todos) {
              node.todos = localTodos || [];
            }
          }
        }
      } catch (error) {
        console.error('Error adding todo:', error);
        setSaveError(t('rightPanel.errorAddingTodo').replace('{error}', error.message));
        // Revert local state on error
        setLocalTodos(localTodos || []);
        if (node.todos) {
          node.todos = localTodos || [];
        }
      } finally {
        setIsAddingTodo(false);
      }
    } else if (!newTodoText.trim()) {
      setSaveError(t('rightPanel.pleaseEnterTodo'));
    } else {
      console.error('Missing required data:', { newTodoText, planId, nodeId: node?.id });
      setSaveError(t('rightPanel.missingDataTodo'));
    }
  };

  const handleStatusChange = async (newStatus) => {

    if (node?.id) {
      setNodeStatus(newStatus);
      if (node.onStatusChange) {
        node.onStatusChange(newStatus);
      }

      if (isDemo) {
        // In demo mode, skip Firestore call
        return;
      }

      if (!planId) {
        return;
      }

      setIsSaving(true);
      try {
        const service = rightPanelService();
        if (service) {
          const result = await service.updateStatus(node.id, newStatus);
          if (!result.success) {
            console.error('Failed to update status:', result.error);
            setSaveError(t('rightPanel.failedToUpdateStatus').replace('{error}', result.error));
            // Revert local state on error
            setNodeStatus(node?.status || 'standby');
          }
        } else {
          console.error('Service is null - planId might be missing');
          setSaveError(t('rightPanel.serviceNotAvailable'));
        }
      } catch (error) {
        console.error('Error updating status:', error);
        setSaveError(t('rightPanel.errorUpdatingStatus').replace('{error}', error.message));
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error('Missing required data:', { planId, nodeId: node?.id });
      setSaveError(t('rightPanel.missingDataStatus'));
    }
  };

  const getDeadlineProgress = (deadline) => {
    if (!deadline) return 0;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const totalDays = 30; // Assume 30 days for progress calculation
    const daysRemaining = Math.max(0, (deadlineDate - now) / (1000 * 60 * 60 * 24));
    const progress = Math.max(0, Math.min(100, ((totalDays - daysRemaining) / totalDays) * 100));
    return progress;
  };

  const getDeadlineStatus = (deadline) => {
    if (!deadline) return { color: '#95a5a6', text: t('rightPanel.noDeadlineSet') };
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysRemaining = (deadlineDate - now) / (1000 * 60 * 60 * 24);

    if (daysRemaining < 0) {
      return { color: '#e74c3c', text: t('rightPanel.overdue') };
    } else if (daysRemaining <= 3) {
      return { color: '#e74c3c', text: t('rightPanel.dueSoon') };
    } else if (daysRemaining <= 7) {
      return { color: '#f39c12', text: t('rightPanel.dueThisWeek') };
    } else if (daysRemaining <= 14) {
      return { color: '#f1c40f', text: t('rightPanel.dueSoon') };
    } else {
      return { color: '#27ae60', text: t('rightPanel.onTrack') };
    }
  };
  if (!node) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e8f2ff 100%)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(52, 152, 219, 0.1)',
          boxShadow: '0 8px 32px rgba(52, 152, 219, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3498db, #2980b9)',
            margin: '0 auto 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: 'white'
          }}>
            📋
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#2c3e50',
            margin: '0',
            letterSpacing: '-0.01em'
          }}>
            {t('rightPanel.selectStep')}
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#5d6d7e',
            margin: '0.5rem 0 0',
            fontWeight: '400'
          }}>
            {t('rightPanel.chooseNode')}
          </p>
        </div>
      </div>
    );
  }

  const sectionStyle = {
    marginBottom: '2rem',
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
    padding: '1.5rem',
    border: '1px solid rgba(52, 152, 219, 0.1)',
    boxShadow: '0 4px 16px rgba(52, 152, 219, 0.08)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.2s ease'
  };

  const sectionTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2c3e50',
    margin: '0 0 1rem 0',
    letterSpacing: '-0.01em',
    borderBottom: '2px solid #3498db',
    paddingBottom: '0.5rem'
  };

  const listStyle = {
    listStyle: 'none',
    padding: '0',
    margin: '0'
  };

  const listItemStyle = {
    padding: '0.75rem 0',
    borderBottom: '1px solid rgba(52, 152, 219, 0.1)',
    fontSize: '16px',
    color: 'black',
    lineHeight: '1.6',
    position: 'relative',
    paddingLeft: '1.5rem'
  };

  const listItemBeforeStyle = {
    content: '""',
    position: 'absolute',
    left: '10px',
    top: '1.7rem',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #3498db, #2980b9)'
  };

  return (
    <>
      <div style={{
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e8f2ff 100%)',
        minHeight: '100vh',
        overflow: 'auto'
      }}>
        {/* Top Control Bar */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(52, 152, 219, 0.1)',
          padding: '0.75rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(52, 152, 219, 0.1)'
        }}>
          {/* Saving indicator and error display */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            {isSaving && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#3498db',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #3498db',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                {t('rightPanel.saving')}
              </div>
            )}
            {saveError && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#e74c3c',
                fontSize: '14px',
                fontWeight: '500',
                background: 'rgba(231, 76, 60, 0.1)',
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid rgba(231, 76, 60, 0.2)'
              }}>
                ⚠️ {saveError}
              </div>
            )}
            {isDemo && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#764ba2',
                fontSize: '14px',
                fontWeight: '500',
                background: 'rgba(118, 75, 162, 0.1)',
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid rgba(118, 75, 162, 0.2)'
              }}>
                🎭 {t('rightPanel.demoMode')}
              </div>
            )}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
                            <button
                  onClick={() => {
                    setAiModalOpen(true);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                  }}
                  title={t('rightPanel.aiExpansionChat')}
                >
                  🤖
                </button>
            <button
              onClick={handleFullscreenToggle}
              style={{
                background: '#e3f0fc',
                color: '#2980b9',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#d1e7fd'}
              onMouseLeave={(e) => e.target.style.background = '#e3f0fc'}
              title={isFullscreen ? t('rightPanel.exitFullscreen') : t('rightPanel.enterFullscreen')}
            >
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="4" width="3" height="2" />
                  <rect x="4" y="4" width="2" height="3" />
                  <rect x="9" y="4" width="3" height="2" />
                  <rect x="10" y="4" width="2" height="3" />
                  <rect x="4" y="10" width="3" height="2" />
                  <rect x="4" y="9" width="2" height="3" />
                  <rect x="9" y="10" width="3" height="2" />
                  <rect x="10" y="9" width="2" height="3" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1V6H2V2H6V1H1Z"/>
                  <path d="M15 15V10H14V14H10V15H15Z"/>
                  <path d="M14 2V6H15V1H10V2H14Z"/>
                  <path d="M2 14V10H1V15H6V14H2Z"/>
                </svg>
              )}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = '#9ca3af';
                }}
                title={t('rightPanel.closePanel')}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div ref={panelRef} style={{ padding: '2rem',   overflowY: 'auto',  height: 'calc(100vh - 130px)' }}>
          {/* Node Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            color: 'white',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              filter: 'blur(40px)'
            }}></div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 0.5rem 0',
              letterSpacing: '-0.02em',
              position: 'relative',
              color: 'white'
            }}>
              {node.label}
            </h1>

            {/* Deadline Management */}
            <div style={{
              marginTop: '1rem',
              position: 'relative'
            }}>
              {(deadlineDate || node?.deadline) && !showDeadlineInput ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    padding: '0.75rem 1rem',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span>📅</span>
                      <span>{t('rightPanel.due')} {new Date(deadlineDate || node?.deadline).toLocaleDateString()}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <div style={{
                        color: getDeadlineStatus(deadlineDate || node?.deadline).color,
                        fontWeight: '600'
                      }}>
                        {getDeadlineStatus(deadlineDate || node?.deadline).text}
                      </div>
                      <button
                        onClick={handleEditDeadline}
                        style={{
                          background: 'rgba(243, 156, 18, 0.8)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(243, 156, 18, 1)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(243, 156, 18, 0.8)'}
                        title={t('rightPanel.editDeadline')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={handleRemoveDeadline}
                        style={{
                          background: 'rgba(231, 76, 60, 0.8)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(231, 76, 60, 1)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(231, 76, 60, 0.8)'}
                        title={t('rightPanel.removeDeadline')}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    height: '8px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{
                      background: `linear-gradient(90deg, ${getDeadlineStatus(deadlineDate || node?.deadline).color}, ${getDeadlineStatus(deadlineDate || node?.deadline).color}88)`,
                      height: '100%',
                      width: `${getDeadlineProgress(deadlineDate || node?.deadline)}%`,
                      borderRadius: '8px',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {showDeadlineInput ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      padding: '0.75rem 1rem',
                      borderRadius: '12px'
                    }}>
                      <input
                        type="date"
                        value={deadlineDate}
                        onChange={(e) => {
                          setDeadlineDate(e.target.value);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem',
                          fontSize: '14px',
                          color: '#2c3e50',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={handleSaveDeadline}
                        disabled={!deadlineDate || isSaving}
                        style={{
                          background: deadlineDate && !isSaving ? '#27ae60' : '#95a5a6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: deadlineDate && !isSaving ? 'pointer' : 'not-allowed',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (deadlineDate && !isSaving) {
                            e.target.style.background = '#229954';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (deadlineDate && !isSaving) {
                            e.target.style.background = '#27ae60';
                          }
                        }}
                      >
                        {isSaving ? t('rightPanel.saving') : t('rightPanel.save')}
                      </button>
                      <button
                        onClick={handleCancelDeadline}
                        disabled={isSaving}
                        style={{
                          background: '#95a5a6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSaving) {
                            e.target.style.background = '#7f8c8d';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSaving) {
                            e.target.style.background = '#95a5a6';
                          }
                        }}
                      >
                        {t('rightPanel.cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setShowDeadlineInput(true);
                        setIsEditingDeadline(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '2px dashed rgba(255, 255, 255, 0.5)',
                        borderRadius: '12px',
                        padding: '0.75rem 1rem',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.7)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                      }}
                    >
                      <span>📅</span>
                      <span>{t('rightPanel.addDeadline')}</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Status Management */}
            <div style={{
              marginTop: '1rem',
              position: 'relative'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                padding: '0.75rem 1rem',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500',
                flexWrap: 'wrap',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginRight: '0.5rem'
                }}>
                  <span>🎯</span>
                  <span>{t('rightPanel.status')}</span>
                </div>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => {
                      handleStatusChange('standby');
                    }}
                    style={{
                      background: nodeStatus === 'standby' ? '#95a5a6' : 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                      if (nodeStatus !== 'standby') {
                        e.target.style.background = 'rgba(255, 255, 255, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (nodeStatus !== 'standby') {
                        e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                      }
                    }}
                    title={t('rightPanel.standbyTooltip')}
                  >
                    {t('rightPanel.standby')}
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange('in-progress');
                    }}
                    style={{
                      background: nodeStatus === 'in-progress' ? '#f39c12' : 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                      if (nodeStatus !== 'in-progress') {
                        e.target.style.background = 'rgba(255, 255, 255, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (nodeStatus !== 'in-progress') {
                        e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                      }
                    }}
                    title={t('rightPanel.inProgressTooltip')}
                  >
                    {t('rightPanel.inProgress')}
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange('done');
                    }}
                    style={{
                      background: nodeStatus === 'done' ? '#27ae60' : 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                      if (nodeStatus !== 'done') {
                        e.target.style.background = 'rgba(255, 255, 255, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (nodeStatus !== 'done') {
                        e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                      }
                    }}
                    title={t('rightPanel.doneTooltip')}
                  >
                    {t('rightPanel.done')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Expansion Loading State */}
          {(isExpanding || expansionError) && needsExpansion(node) && (
            <div style={{
              ...sectionStyle,
              background: expansionError 
                ? 'linear-gradient(135deg, rgba(231, 76, 60, 0.1) 0%, rgba(192, 57, 43, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)',
              border: `1px solid ${expansionError ? 'rgba(231, 76, 60, 0.3)' : 'rgba(52, 152, 219, 0.3)'}`,
              textAlign: 'center',
              padding: '2rem',
            }}>
              {isExpanding ? (
                <>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid rgba(52, 152, 219, 0.3)',
                    borderTop: '3px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 1rem',
                  }} />
                  <p style={{ 
                    fontSize: '16px', 
                    color: '#2c3e50',
                    margin: 0,
                    fontWeight: '500'
                  }}>
                    {getLoadingMessage(expansionPhase)}
                  </p>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#7f8c8d',
                    margin: '0.5rem 0 0',
                  }}>
                    {t('rightPanel.loadingDetails', 'Generating personalized guidance for this step...')}
                  </p>
                </>
              ) : expansionError && (
                <>
                  <div style={{
                    fontSize: '32px',
                    marginBottom: '1rem',
                  }}>
                    ⚠️
                  </div>
                  <p style={{ 
                    fontSize: '16px', 
                    color: '#e74c3c',
                    margin: '0 0 1rem',
                    fontWeight: '500'
                  }}>
                    {t('rightPanel.expansionError', 'Failed to load step details')}
                  </p>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#7f8c8d',
                    margin: '0 0 1rem',
                  }}>
                    {expansionError}
                  </p>
                  <button
                    onClick={handleRetryExpansion}
                    style={{
                      background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    {t('rightPanel.retryExpansion', '🔄 Retry Loading')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* details */}
          {node.details && (
            <div style={sectionStyle}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>{t('rightPanel.overview')}</h2>
              </div>
              <p style={{
                fontSize: '16px',
                color: 'black',
                lineHeight: '1.7',
                margin: '0',
                fontWeight: '400'
              }}>
                {node.details}
              </p>
            </div>
          )}

          {/* Instructions */}
          {node.instructions?.length > 0 && (
            <div style={sectionStyle}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>{t('rightPanel.instructions')}</h2>
              </div>
              <ul style={listStyle}>
                {node.instructions.map((item, i) => (
                  <li key={i} style={listItemStyle}>
                    <span style={listItemBeforeStyle}></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* SOPs */}
          {node.sops?.length > 0 && (
            <div style={{
              ...sectionStyle,
              borderLeft: '4px solid #3498db'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>{t('rightPanel.standardOperatingProcedures')}</h2>
              </div>
              <ul style={listStyle}>
                {node.sops.map((sop, i) => (
                  <li key={i} style={{
                    ...listItemStyle,
                    background: 'rgba(52, 152, 219, 0.05)',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '0.5rem',
                    border: '1px solid rgba(52, 152, 219, 0.1)'
                  }}>
                    <span style={{
                      ...listItemBeforeStyle,
                      background: '#3498db',
                      top: '1.25rem'
                    }}></span>
                    {sop}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* To-Do List */}
          <div style={{
            ...sectionStyle,
            borderLeft: '4px solid #27ae60'
          }}>
            <h2 style={sectionTitleStyle}>{t('rightPanel.todo')}</h2>

            {/* Add New Todo Section */}
            <div style={{
              background: 'rgba(39, 174, 96, 0.1)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              border: '1px solid rgba(39, 174, 96, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#27ae60' }}> {t('rightPanel.addNewTodo')}</span>
              </div>
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  value={newTodoText}
                  onChange={(e) => setNewTodoText(e.target.value)}
                  placeholder={t('rightPanel.enterTodoPlaceholder')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newTodoText.trim()) {
                      handleAddNewTodo();
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid rgba(39, 174, 96, 0.3)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    background: 'rgba(255, 255, 255, 0.9)',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#27ae60'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(39, 174, 96, 0.3)'}
                />
                <button
                  onClick={() => {
                    handleAddNewTodo();
                  }}
                  disabled={!newTodoText.trim() || isAddingTodo}
                  style={{
                    background: newTodoText.trim() && !isAddingTodo ? '#27ae60' : '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.75rem 1rem',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: newTodoText.trim() && !isAddingTodo ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (newTodoText.trim() && !isAddingTodo) {
                      e.target.style.background = '#229954';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (newTodoText.trim() && !isAddingTodo) {
                      e.target.style.background = '#27ae60';
                    }
                  }}
                >
                  {isAddingTodo ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      {t('rightPanel.adding')}
                    </>
                  ) : (
                    t('rightPanel.add')
                  )}
                </button>
              </div>
            </div>

            {/* Existing Todos List */}
            {localTodos.length > 0 && (
              <ul style={listStyle}>
                {localTodos.map((todo) => (
                  <li key={todo.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem',
                    background: todo.done ? 'rgba(39, 174, 96, 0.1)' : 'rgba(52, 152, 219, 0.05)',
                    borderRadius: '8px',
                    marginBottom: '0.5rem',
                    border: `1px solid ${todo.done ? 'rgba(39, 174, 96, 0.2)' : 'rgba(52, 152, 219, 0.1)'}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="checkbox"
                      checked={todo.done}
                      onChange={() => {
                        handleTodoToggle(todo.id);
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        marginRight: '1rem',
                        accentColor: '#27ae60',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontSize: '16px',
                      color: todo.done ? '#27ae60' : '#2c3e50',
                      textDecoration: todo.done ? 'line-through' : 'none',
                      fontWeight: todo.done ? '500' : '400',
                      flex: 1
                    }}>
                      {todo.text}
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteTodoModal({ show: true, todoId: todo.id, todoText: todo.text });
                      }}
                      style={{
                        background: 'rgba(231, 76, 60, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(231, 76, 60, 1)';
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(231, 76, 60, 0.8)';
                        e.target.style.transform = 'scale(1)';
                      }}
                      title={t('rightPanel.removeTodoTooltip').replace('{text}', todo.text)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* No Todos Message */}
            {localTodos.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#95a5a6',
                fontStyle: 'italic',
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '8px',
                border: '1px dashed rgba(39, 174, 96, 0.3)'
              }}>
                {t('rightPanel.noTodosYet')}
              </div>
            )}
          </div>

          {/* Tips */}
          {node.tips?.length > 0 && (
            <div style={{
              ...sectionStyle,
              background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(52, 152, 219, 0.1) 100%)',
              borderLeft: '4px solid #3498db'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>{t('rightPanel.tips')}</h2>
              </div>
              <ul style={listStyle}>
                {node.tips.map((tip, i) => (
                  <li key={i} style={{
                    ...listItemStyle,
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '0.5rem',
                    border: '1px solid rgba(52, 152, 219, 0.2)'
                  }}>
                    <span style={{
                      ...listItemBeforeStyle,
                      background: '#f39c12',
                      top: '1.25rem'
                    }}></span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Resources */}
          {node.resources?.length > 0 && (
            <div style={sectionStyle}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>
                  {t('rightPanel.resources')}
                  <span
                    style={{
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#3498db',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      lineHeight: '20px',
                      marginLeft: '8px',
                      cursor: 'help',
                      position: 'relative'
                    }}
                    title={t('rightPanel.resourcesTooltip')}
                  >
                    ?
                  </span>
                </h2>
              </div>
              <ul style={listStyle}>
                {node.resources.map((res, i) => (
                  <li key={i} style={{
                    padding: '0.75rem',
                    background: 'rgba(52, 152, 219, 0.05)',
                    borderRadius: '8px',
                    marginBottom: '0.5rem',
                    border: '1px solid rgba(52, 152, 219, 0.1)',
                    transition: 'all 0.2s ease'
                  }}>
                    <a
                      href={res.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#3498db',
                        textDecoration: 'none',
                        fontSize: '16px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.color = '#2980b9'}
                      onMouseOut={(e) => e.target.style.color = '#3498db'}
                    >
                      🔗 {res.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Metrics */}
          {node.metrics?.length > 0 && (
            <div style={{
              ...sectionStyle,
              borderLeft: '4px solid #e74c3c'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>{t('rightPanel.metrics')}</h2>

              </div>
              <ul style={listStyle}>
                {node.metrics.map((metric, i) => (
                  <li key={i} style={{
                    ...listItemStyle,
                    background: 'rgba(231, 76, 60, 0.05)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '0.5rem',
                    border: '1px solid rgba(231, 76, 60, 0.1)'
                  }}>
                    <span style={{
                      ...listItemBeforeStyle,
                      background: '#e74c3c',
                      top: '1.25rem'
                    }}></span>
                    {metric}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* FAQ */}
          {node.faq?.length > 0 && (
            <div style={sectionStyle}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={sectionTitleStyle}>{t('rightPanel.faq')}</h2>
              </div>
              {node.faq.map((item, i) => (
                <div key={i} style={{
                  marginBottom: '1rem',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid rgba(52, 152, 219, 0.1)'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #3498db, #2980b9)',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    {t('rightPanel.question')} {item.q}
                  </div>
                  <div style={{
                    background: 'rgba(52, 152, 219, 0.05)',
                    padding: '0.75rem 1rem',
                    fontSize: '16px',
                    color: '#2c3e50',
                    lineHeight: '1.6'
                  }}>
                    {t('rightPanel.answer')} {item.a}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div style={{
            ...sectionStyle,
            background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.05) 0%, rgba(243, 156, 18, 0.1) 100%)',
            borderLeft: '4px solid #f39c12'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h2 style={sectionTitleStyle}>{t('rightPanel.notes')}</h2>
              {!isEditingNotes && (
                <button
                  onClick={() => setIsEditingNotes(true)}
                  style={{
                    background: '#f39c12',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.5rem 1rem',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#e67e22'}
                  onMouseLeave={(e) => e.target.style.background = '#f39c12'}
                >
                  {t('rightPanel.edit')}
                </button>
              )}
            </div>

            {isEditingNotes ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder={t('rightPanel.notesPlaceholder')}
                  style={{
                    fontSize: '16px',
                    color: '#2c3e50',
                    lineHeight: '1.7',
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '2px solid #f39c12',
                    minHeight: '120px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    outline: 'none'
                  }}
                />
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={handleCancelNotes}
                    style={{
                      background: '#95a5a6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#7f8c8d'}
                    onMouseLeave={(e) => e.target.style.background = '#95a5a6'}
                  >
                    {t('rightPanel.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      handleSaveNotes();
                    }}
                    style={{
                      background: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#229954'}
                    onMouseLeave={(e) => e.target.style.background = '#27ae60'}
                  >
                    {t('rightPanel.save')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                fontSize: '16px',
                color: '#2c3e50',
                lineHeight: '1.7',
                margin: '0',
                fontWeight: '400',
                background: 'rgba(255, 255, 255, 0.7)',
                padding: '1rem',
                borderRadius: '8px',
                border: '1px solid rgba(243, 156, 18, 0.2)',
                minHeight: '60px',
                whiteSpace: 'pre-wrap'
              }}>
                {notesText || (
                  <span style={{ color: '#95a5a6', fontStyle: 'italic' }}>
                    {t('rightPanel.notesEmpty')}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* CSS Animations */}
          <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
       `}</style>
        </div>

        {/* AI Expansion Modal */}
        <AIExpansionModal
          isOpen={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          sectionContent={node?.details || node?.instructions?.join('\n') || node?.label || ''}
          fullContent={JSON.stringify(node)}
          sectionTitle={node?.label || 'Node'}
          pageName={`plan-${planId}-node-${node?.id}`}
        />

        {/* Delete Todo Confirmation Modal */}
        <Modal
          isOpen={deleteTodoModal.show}
          onClose={() => setDeleteTodoModal({ show: false, todoId: null, todoText: '' })}
          type="warning"
          title={t('rightPanel.removeTodoConfirmTitle', 'Remove Todo Item')}
          message={t('rightPanel.removeTodoConfirm').replace('{text}', deleteTodoModal.todoText)}
          onConfirm={() => {
            if (deleteTodoModal.todoId) {
              handleRemoveTodo(deleteTodoModal.todoId);
            }
            setDeleteTodoModal({ show: false, todoId: null, todoText: '' });
          }}
          confirmButtonText={t('rightPanel.removeTodoConfirmButton', 'Remove')}
          cancelButtonText={t('rightPanel.removeTodoCancelButton', 'Cancel')}
        />
      </div>
    </>
  );
};

export default RightPanelRenderer;